<script setup>
// Right Chat - tela do Kanban proprio.
// Consome so o que docs/kanban-contrato.md ja expoe: nenhum endpoint novo foi criado
// para esta tela.
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import RightKanbanColumn from './RightKanbanColumn.vue';
import RightKanbanBoardDialog from './RightKanbanBoardDialog.vue';
import RightKanbanStepDialog from './RightKanbanStepDialog.vue';
import { useRightKanban } from '../../../composables/useRightKanban';

const route = useRoute();
const router = useRouter();

const {
  boards,
  board,
  tasks,
  currentBoardId,
  columns,
  steps,
  hasBoards,
  hasSteps,
  isEmptyBoard,
  totalValue,
  loadingBoards,
  loadingBoard,
  error,
  clearError,
  fetchBoards,
  fetchBoard,
  selectBoard,
  moveTask,
} = useRightKanban();

const accountId = computed(() => route.params.accountId);
const draggingTaskId = ref(null);
const boardDialog = ref(null); // null | { board: Object|null }
const stepDialog = ref(null); // null | { step: Object|null }

const isLoading = computed(() => loadingBoards.value || loadingBoard.value);
const formattedTotal = computed(() =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(totalValue.value || 0)
);
const nextPosition = computed(() =>
  steps.value.length
    ? Math.max(...steps.value.map(step => step.position || 0)) + 1
    : 0
);

const load = async () => {
  await fetchBoards();
  const routeBoardId = Number(route.params.boardId);
  if (routeBoardId && routeBoardId !== currentBoardId.value) {
    await selectBoard(routeBoardId);
  } else {
    await fetchBoard(currentBoardId.value);
  }
};

onMounted(load);

// A URL segue o board escolhido para o link ser compartilhavel; `replace` para nao
// entupir o historico do navegador a cada troca no seletor.
watch(currentBoardId, boardId => {
  if (!boardId) return;
  if (Number(route.params.boardId) === boardId) return;
  const isRightRoute = route.name && String(route.name).startsWith('right_kanban');
  router.replace({
    name: isRightRoute ? 'right_kanban_board' : 'kanban_board',
    params: { accountId: accountId.value, boardId },
  });
});

const onSelectBoard = event => selectBoard(event.target.value);

const onDragStart = task => {
  draggingTaskId.value = task.id;
};

const onDragEnd = () => {
  draggingTaskId.value = null;
};

const onDropCard = async ({ stepId, beforeTaskId }) => {
  const taskId = draggingTaskId.value;
  draggingTaskId.value = null;
  if (!taskId) return;
  await moveTask({ taskId, toStepId: stepId, insertBeforeTaskId: beforeTaskId });
};

const openStepDialog = (step = null) => {
  stepDialog.value = { step };
};

const closeStepDialog = () => {
  stepDialog.value = null;
};

const openBoardDialog = (target = null) => {
  boardDialog.value = { board: target };
};

const closeBoardDialog = () => {
  boardDialog.value = null;
};
</script>

<template>
  <div class="rk-page">
    <header class="rk-page__head">
      <div class="rk-page__title-area">
        <h1 class="rk-page__title">Kanban</h1>
        <span v-if="board" class="rk-page__summary">
          {{ tasks.length }} card(s) · {{ formattedTotal }}
        </span>
      </div>

      <div class="rk-page__actions">
        <select
          v-if="hasBoards"
          class="rk-select"
          :value="currentBoardId || ''"
          :disabled="isLoading"
          @change="onSelectBoard"
        >
          <option v-for="item in boards" :key="item.id" :value="item.id">
            {{ item.name }}
          </option>
        </select>

        <button
          v-if="board"
          type="button"
          class="rk-btn"
          @click="openBoardDialog(board)"
        >
          Editar board
        </button>
        <button type="button" class="rk-btn" @click="openBoardDialog(null)">
          Novo board
        </button>
        <button
          v-if="board"
          type="button"
          class="rk-btn rk-btn--primary"
          @click="openStepDialog(null)"
        >
          Nova etapa
        </button>
        <button
          type="button"
          class="rk-btn"
          :disabled="isLoading"
          @click="fetchBoard()"
        >
          Atualizar
        </button>
      </div>
    </header>

    <div v-if="error" class="rk-banner">
      <span class="rk-banner__text">{{ error }}</span>
      <button type="button" class="rk-banner__btn" @click="load">
        Tentar de novo
      </button>
      <button type="button" class="rk-banner__btn" @click="clearError">
        Dispensar
      </button>
    </div>

    <div v-if="isLoading" class="rk-board rk-board--skeleton">
      <div v-for="n in 4" :key="n" class="rk-skeleton-column">
        <span class="rk-skeleton rk-skeleton--title" />
        <span class="rk-skeleton rk-skeleton--card" />
        <span class="rk-skeleton rk-skeleton--card" />
      </div>
    </div>

    <div v-else-if="!hasBoards" class="rk-empty">
      <h2 class="rk-empty__title">Nenhum board ainda</h2>
      <p class="rk-empty__text">
        Crie o primeiro funil. Depois adicione as etapas — e a descricao de cada etapa,
        que e o que ensina a IA quando mover o card.
      </p>
      <button type="button" class="rk-btn rk-btn--primary" @click="openBoardDialog(null)">
        Criar board
      </button>
    </div>

    <div v-else-if="!hasSteps" class="rk-empty">
      <h2 class="rk-empty__title">Board sem etapas</h2>
      <p class="rk-empty__text">
        Um funil precisa de pelo menos uma etapa para receber card.
      </p>
      <button type="button" class="rk-btn rk-btn--primary" @click="openStepDialog(null)">
        Criar etapa
      </button>
    </div>

    <template v-else>
      <p v-if="isEmptyBoard" class="rk-hint">
        Nenhum card neste board ainda. Os cards nascem das conversas — pela IA ou pela
        API do Kanban.
      </p>

      <div class="rk-board">
        <RightKanbanColumn
          v-for="column in columns"
          :key="column.step.id"
          :step="column.step"
          :tasks="column.tasks"
          :account-id="accountId"
          :dragging-task-id="draggingTaskId"
          @drag-start="onDragStart"
          @drag-end="onDragEnd"
          @drop-card="onDropCard"
          @edit-step="openStepDialog"
        />
      </div>
    </template>

    <RightKanbanBoardDialog
      v-if="boardDialog"
      :board="boardDialog.board"
      @close="closeBoardDialog"
    />

    <RightKanbanStepDialog
      v-if="stepDialog && currentBoardId"
      :board-id="currentBoardId"
      :step="stepDialog.step"
      :next-position="nextPosition"
      @close="closeStepDialog"
    />
  </div>
</template>

<style scoped>
.rk-page {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  height: 100%;
  padding: 16px;
  overflow: hidden;
  background: #ffffff;
}

.rk-page__head {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
}

.rk-page__title-area {
  display: flex;
  gap: 10px;
  align-items: baseline;
}

.rk-page__title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.rk-page__summary {
  font-size: 12px;
  color: #64748b;
}

.rk-page__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.rk-select {
  padding: 7px 10px;
  font-size: 13px;
  color: #0f172a;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
}

.rk-btn {
  padding: 7px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
}

.rk-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.rk-btn--primary {
  color: #ffffff;
  background: #4f46e5;
  border-color: #4f46e5;
}

.rk-banner {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  padding: 8px 12px;
  font-size: 12px;
  color: #b91c1c;
  background: #fee2e2;
  border-radius: 8px;
}

.rk-banner__text {
  flex: 1;
}

.rk-banner__btn {
  padding: 3px 8px;
  font-size: 12px;
  font-weight: 600;
  color: #b91c1c;
  cursor: pointer;
  background: transparent;
  border: 1px solid #fecaca;
  border-radius: 6px;
}

.rk-hint {
  margin: 0;
  font-size: 12px;
  color: #64748b;
}

.rk-board {
  display: flex;
  flex: 1;
  gap: 12px;
  align-items: flex-start;
  padding-bottom: 8px;
  overflow-x: auto;
  overflow-y: hidden;
}

.rk-board > * {
  max-height: 100%;
}

.rk-empty {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 32px;
  text-align: center;
}

.rk-empty__title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.rk-empty__text {
  max-width: 420px;
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.rk-skeleton-column {
  display: flex;
  flex: 0 0 288px;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  background: #f1f5f9;
  border-radius: 10px;
}

.rk-skeleton {
  display: block;
  background: #e2e8f0;
  border-radius: 6px;
  animation: rk-pulse 1.4s ease-in-out infinite;
}

.rk-skeleton--title {
  width: 55%;
  height: 14px;
}

.rk-skeleton--card {
  height: 56px;
}

@keyframes rk-pulse {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.45;
  }
}

.dark .rk-page {
  background: #0f172a;
}

.dark .rk-page__title,
.dark .rk-empty__title {
  color: #e2e8f0;
}

.dark .rk-page__summary,
.dark .rk-empty__text,
.dark .rk-hint {
  color: #94a3b8;
}

.dark .rk-select,
.dark .rk-btn {
  color: #cbd5e1;
  background: #1e293b;
  border-color: #334155;
}

.dark .rk-btn--primary {
  color: #ffffff;
  background: #4f46e5;
  border-color: #4f46e5;
}

.dark .rk-skeleton-column {
  background: #1e293b;
}

.dark .rk-skeleton {
  background: #334155;
}
</style>
